import captureError from '@disco/utils/captureError'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { User } from 'types/user'
import api from '@disco/api/server'
import { getUser } from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'
import DocumentHeadTags from 'components/document-head-tags'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import InlineError from 'components/ui/inline-error'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'
const SUBJECT_OPTIONS = [
  'Upgrade my account',
  'Find out about the Enterprise plan',
  'Request a demo',
  'Other',
]
type ServerError = {
  status: 'error'
  message: Record<string, string[]>
}

const parseServerError = (error: ServerError) => {
  let result = {}

  for (const key of Object.keys(error.message)) {
    result[key] = error.message[key][0]
  }

  return result
}

const ContactSales = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user: User = useSelector(getUser)
  const [errors, setErrors] = useState<
    Record<string, string | null | undefined>
  >({})
  const hasErrors: boolean = useMemo(() => {
    return Object.keys(errors).some((key: string) => Boolean(errors[key]))
  }, [errors])
  const [formState, setFormState] = useState<{
    name: string
    from_email: string
    phone: string
    subject: string
    description: string
  }>({
    name: '',
    from_email: user.email,
    phone: '',
    subject: '',
    description: '',
  })
  const [isAwaiting, setIsAwaiting] = useState<boolean>(false)
  const handleCancel = useCallback(() => {
    navigate('/')
  }, [navigate])
  const handleFieldChange = useCallback(
    (event: {
      target: {
        name: string
        value: string
        required: boolean
      }
    }) => {
      const { name, value, required } = event.target
      let newErrors = { ...errors }

      if (required && !value) {
        newErrors[name] = 'This field is required.'
      } else {
        newErrors[name] = null
      }

      setErrors(newErrors)
      setFormState(state => ({ ...state, [name]: value }))
    },
    [errors],
  )
  const handleSubmit = useCallback(async () => {
    let newErrors = { ...errors }
    const requiredFields = ['name', 'from_email', 'phone', 'subject']
    requiredFields.forEach(field => {
      if (!formState[field]) {
        newErrors[field] = 'This field is required.'
      }
    })

    if (formState.subject === 'Other' && !formState.description) {
      newErrors.description = 'This field is required.'
    }

    setErrors(newErrors)

    if (!Object.values(newErrors).some(Boolean) && !isAwaiting) {
      try {
        setIsAwaiting(true)
        await api.contactUs({
          type: 'sales',
          body: `${formState.name}\n${formState.phone}\n${
            formState.subject === 'Other'
              ? "I'd like to talk about: " + formState.description
              : ''
          }`,
          subject: formState.subject,
          from_email: formState.from_email,
        })
        navigate('/')
        dispatch(
          pushUINotification({
            level: 'success',
            message: 'Email sent!',
          }),
        )
      } catch (error) {
        if (error.body) {
          setErrors(parseServerError(error.body))
        }

        captureError(error)
      }

      setIsAwaiting(false)
    }
  }, [dispatch, formState, errors, isAwaiting])
  return (
    <ModalDialog
      title="Get in touch"
      primaryAction={{
        caption: 'Submit',
        disabled: hasErrors,
        progressCaption: 'Sending',
        progressIndicator: isAwaiting,
        onClick: handleSubmit,
      }}
      width="small"
      onCancel={handleCancel}
    >
      <DocumentHeadTags title="Get in touch | DISCO" />
      <Stack space="small">
        <TextField
          autoFocus
          compact={false}
          error={errors?.name}
          label="Your name"
          name="name"
          required
          onBlur={handleFieldChange}
          onChange={handleFieldChange}
        />
        <TextField
          compact={false}
          error={errors?.from_email}
          label="Your user email"
          name="from_email"
          placeholder="example@record-label.com"
          required
          value={formState.from_email}
          readOnly={Boolean(user.email)}
          disabled={Boolean(user.email)}
          onBlur={handleFieldChange}
          onChange={handleFieldChange}
        />
        <TextField
          compact={false}
          error={errors?.phone}
          label="Phone"
          required
          name="phone"
          type="tel"
          value={formState.phone}
          onBlur={handleFieldChange}
          onChange={event => {
            const value = event.target.value

            if (/^\+?([0-9\s-\(\)]?)+$/.test(value) || !value) {
              handleFieldChange(event)
            }
          }}
        />
        <>
          <Label caption="How can we help?" required />
          <CustomDropdown
            placeholder="Select"
            options={SUBJECT_OPTIONS.map((subject: string) => ({
              label: subject,
              value: subject,
            }))}
            value={formState.subject}
            onBlur={option => {
              // react-select doesn't return input value
              if (!option?.value) {
                if (formState.subject === '') {
                  handleFieldChange({
                    target: {
                      name: 'subject',
                      value: '',
                      required: true,
                    },
                  })
                }
              }
            }}
            onChange={option => {
              const newValue = option?.value ?? ''
              handleFieldChange({
                target: {
                  name: 'subject',
                  value: newValue,
                  required: true,
                },
              })
              setFormState(state => ({
                ...state,
                subject: newValue,
                description: newValue !== 'Other' ? '' : state.description,
              }))
              setErrors(prevErrors => ({
                ...prevErrors,
                description:
                  newValue === 'Other' ? prevErrors.description : null,
              }))
            }}
          />
          <InlineError error={errors?.subject} status="danger" />
        </>
        {formState.subject === 'Other' && (
          <TextField
            autoFocus
            compact={false}
            error={errors?.description}
            label="Description"
            multiline
            name="description"
            required={formState.subject === 'Other'}
            value={formState.description}
            onBlur={handleFieldChange}
            onChange={handleFieldChange}
          />
        )}
      </Stack>
    </ModalDialog>
  )
}

export default ContactSales
